import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Frame, Navigation, Text, Page, Card, Button, Spinner } from '@shopify/polaris';
import { HomeMinor, ProductsMinor, CustomersMinor, SettingsMinor, QuestionMarkMajor } from '@shopify/polaris-icons';

import { APP_VERSION } from './version';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Customers from './pages/Customers';
import Settings from './pages/Settings';
import Help from './pages/Help';
import { apiHelpers } from './utils/api';
import { startSubscription } from './utils/billing';

// Wordmark (icon + "ORDER GUARD"), served from web/frontend/public/logo-wordmark.png
// (copied to the build root at build time). The square logo.png stays the favicon — see index.html.
const logoSrc = `${process.env.PUBLIC_URL}/logo-wordmark.png`;

// Shown instead of the app until the shop has an active plan. Sends the merchant
// straight to Shopify's charge approval page; the button is there in case the
// redirect is blocked, fails, or they come back without approving.
function ChoosePlan({ onSubscribed }) {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const start = () => {
    setStarting(true);
    setError(null);
    startSubscription()
      .then((alreadyActive) => {
        if (alreadyActive) onSubscribed();
      })
      .catch(() => {
        setError("Couldn't reach Shopify's billing page. Please try again.");
        setStarting(false);
      });
  };

  useEffect(start, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Page narrowWidth>
      <Card>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <img src={logoSrc} alt="Order Guard" style={{ maxWidth: '200px', marginBottom: '16px' }} />
          <Text variant="headingLg" as="h1">Start your Order Guard plan</Text>
          <div style={{ margin: '12px 0 20px' }}>
            <Text as="p" color="subdued">
              Standard plan, $10 USD every 30 days, billed through Shopify. Approve the charge on
              Shopify's billing page and you'll come straight back here.
            </Text>
          </div>
          {error && (
            <div style={{ marginBottom: '16px' }}>
              <Text as="p" color="critical">{error}</Text>
            </div>
          )}
          <Button primary loading={starting} onClick={start}>
            Start plan
          </Button>
        </div>
      </Card>
    </Page>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  // null while checking; { active, plan } once known.
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    apiHelpers.getBillingStatus()
      .then((res) => setBilling(res.data))
      // Fail open: if we can't read the subscription, don't lock a possibly paying
      // merchant out of their own limits.
      .catch((err) => {
        console.error('Billing status check failed:', err && err.message);
        setBilling({ active: true, plan: null });
      });
  }, []);

  if (!billing) {
    return (
      <div style={{ padding: '80px', textAlign: 'center' }}>
        <Spinner size="large" />
      </div>
    );
  }

  if (!billing.active) {
    return <ChoosePlan onSubscribed={() => setBilling({ ...billing, active: true })} />;
  }

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      {/* App Info Section */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #e1e3e5',
        marginBottom: '16px'
      }}>
        <img
          src={logoSrc}
          alt="Order Guard"
          style={{ width: '100%', maxWidth: '200px', height: 'auto', display: 'block', margin: '0 auto 8px' }}
        />
        <Text variant="bodyXs" as="p" color="subdued" alignment="center">
          v{APP_VERSION}
        </Text>
      </div>

      <Navigation.Section
        items={[
          {
            label: 'Home',
            icon: HomeMinor,
            onClick: () => navigate('/'),
            selected: location.pathname === '/',
          },
          {
            label: 'Product Limits',
            icon: ProductsMinor,
            onClick: () => navigate('/products'),
            selected: location.pathname === '/products',
          },
          {
            label: 'Customers',
            icon: CustomersMinor,
            onClick: () => navigate('/customers'),
            selected: location.pathname === '/customers',
          },
          {
            label: 'Settings',
            icon: SettingsMinor,
            onClick: () => navigate('/settings'),
            selected: location.pathname === '/settings',
          },
          {
            label: 'Help',
            icon: QuestionMarkMajor,
            onClick: () => navigate('/help'),
            selected: location.pathname === '/help',
          },
        ]}
      />
    </Navigation>
  );

  return (
    <Frame
      navigation={navigationMarkup}
      showMobileNavigation={false}
    >
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/products" element={<Products />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/help" element={<Help />} />
      </Routes>
    </Frame>
  );
}

export default App;
